import { toast } from 'vue-sonner'
import type { ProductModelV2 } from '@develop/ia-api-library'

import IaNotificationPrescription from './NotificationPrescription.vue'
import IaNotificationAddToCartProduct from './NotificationAddToCartProduct.vue'
import IaNotificationAlert from './NotificationAlert.vue'

import type { PrescriptionVariant } from '@/models/prescription'
import type { AlertProps } from '@/models/global-alert'

const NOTIFICATION_DURATION = 3000

const { smDown } = useIaBreakpoints()
const toasterOptions = ref()

// Ensure that toast container doesn't have any additional styles
const toastOptionsStyle = {
  padding: '0',
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  display: 'block',
}

const addToCartStyle = smDown.value
  ? {
      right: '0',
      left: '0',
      top: '0',
      width: '100%',
    }
  : {
      'right': '48px',
      '--offset': '24px',
    }

/**
 * Add product to cart
 *
 * @param product product to add
 * @param quantity quantity to add
 */
const addToCart = (product: ProductModelV2, quantity = 1) => {
  set(toasterOptions, {
    'position': 'top-right',
    'style': addToCartStyle,
    'toast-options': {
      style: {
        ...toastOptionsStyle,
        width: smDown.value ? '100%' : 'auto',
      },
    },
  })

  const vNode = h(IaNotificationAddToCartProduct, {
    product,
    quantity,
    couponCode: product.coupon?.couponCode,
  })

  toast.custom(shallowRef(vNode), {
    duration: NOTIFICATION_DURATION,
  })
}

/**
 * Add prescription to cart
 *
 * @param variant type of prescription [prescription || ePrescription]
 * @param quantity quantity to add
 */
const addPrescriptionToCart = (variant: PrescriptionVariant, quantity = 1) => {
  set(toasterOptions, {
    'position': 'top-right',
    'style': addToCartStyle,
    'toast-options': {
      style: {
        ...toastOptionsStyle,
        width: smDown.value ? '100%' : 'auto',
      },
    },
  })

  const vNode = h(IaNotificationPrescription, {
    variant,
    quantity,
    class: '!w-full',
  })

  toast.custom(shallowRef(vNode), {
    duration: NOTIFICATION_DURATION,
  })
}

/**
 * Display alert notification message
 *
 * When using this function ensure that is called only on client side. (process.client)
 *
 * @param props Props for alert component
 */
const alertMessage = (props: AlertProps) => {
  set(toasterOptions, {
    'position': 'top-center',
    'style': {
      '--width': '100%',
      '--front-toast-height': 'fit-content',
      '--offset': '24px',
      'display': 'flex',
      'justify-content': 'space-around',
    },
    'toast-options': {
      style: toastOptionsStyle,
    },
  })

  const vNode = h(IaNotificationAlert, {

    type: props.type,
    closable: props.closable,
    icon: props.icon,
    actionText: props.actionText,
    label: props.label,
  },
  )

  toast.custom(shallowRef(vNode), {
    duration: NOTIFICATION_DURATION,
  })
}

export const launchNotification = {
  toasterOptions,
  addToCart,
  alertMessage,
  addPrescriptionToCart,
}
